<template>
  <b-container id="object-page" fluid="md">
    <h1>
      {{ title }}
    </h1>
    <base-detail-modals
      v-model="modals"
      @delete-entity="deleteEntity"
      @delete-draft="deleteDraft"
      @publish="publish"
      @cancel-leave="leaveGuard.modalActive = false"
      @perform-leave="$router.push(leaveGuard.to)"
    />

    <b-form ref="form" :validated="wasValidated">
      <div id="form-block" role="tablist">
        <b-row>
          <b-col sm="auto">
            {{ $t("pages.physicalObjects.status") }}
          </b-col>
          <b-col sm="auto">
            <b-form-checkbox
              v-model="draft.extant"
              name="form-element-extant"
              switch
              @change="changed"
            >
              {{
                draft.extant
                  ? $t("pages.physicalObjects.extant")
                  : $t("pages.physicalObjects.nonextant")
              }}
            </b-form-checkbox>
          </b-col>
        </b-row>
        <collapsible-card :header="$t('pages.physicalObjects.inscriptions')">
          <label for="form-element-inscriptions">
            {{ $t("pages.physicalObjects.inscriptions") }}
          </label>
          <div
            v-if="values.inscriptions && values.inscriptions.length > 0"
            id="form-element.inscriptions"
          >
            <b-row v-for="(item, index) in values.inscriptions" :key="index">
              <b-col>
                <router-link
                  :to="{
                    name: 'inscription',
                    params: { id: item.id }
                  }"
                >
                  {{ inscription(item) }}
                </router-link>
              </b-col>
            </b-row>
          </div>
          <div
            v-if="values.inscriptions && values.inscriptions.length == 0"
            id="form-element.inscriptions"
          >
            {{ $t("pages.physicalObjects.noinscriptions") }}
          </div>
        </collapsible-card>
        <collapsible-card :header="$t('pages.physicalObjects.artefacts')">
          <div class="artefact">
            {{ $t("pages.physicalObjects.artefact") }}
            <b-form-input
              id="form-element-artefact"
              v-model="draft['artefact']"
              type="text"
              @change="changed"
              @input="changed"
            />
          </div>
          <hr />
          <div class="artefacts">
            {{ $t("pages.physicalObjects.artefacts") }}
            <artefacts-selector
              v-if="draft['artefacts']"
              id="form-element-artefacts"
              v-model="draft['artefacts']"
              @change="changed"
            />
          </div>
        </collapsible-card>
        <collapsible-card :header="$t('pages.physicalObjects.material')">
          <div class="material">
            {{ $t("pages.physicalObjects.materialDescription") }}:
            <b-form-input
              id="form-element-material"
              v-model="draft['material']"
              type="text"
              @change="changed"
              @input="changed"
            />
          </div>
          <hr />
          <div class="materials">
            {{ $t("pages.physicalObjects.material") }}:
            <materials-selector
              v-if="draft['materials']"
              id="form-element-materials"
              v-model="draft['materials']"
              @change="changed"
            />
          </div>
        </collapsible-card>
        <collapsible-card :header="$t('pages.physicalObjects.period')">
          <div class="object-termini">
            <label for="form-element-tpq">
              {{ $t("pages.physicalObjects.tpq") }}
            </label>
            <b-form-input
              id="form-element-tpq"
              v-model="draft['tpq']"
              type="number"
              @change="changed"
              @input="changed"
            />
            <hr />
            <label for="form-element-taq">
              {{ $t("pages.physicalObjects.taq") }}
            </label>
            <b-form-input
              id="form-element-taq"
              v-model="draft['taq']"
              type="number"
              @change="changed"
              @input="changed"
            />
          </div>
          <hr />
          <label for="form-element-datings">
            {{ $t("pages.physicalObjects.datings") }}
          </label>
          <datings-component
            v-if="draft['datings']"
            id="form-element-datings"
            v-model="draft['datings']"
            @change="changed"
          />
        </collapsible-card>
        <collapsible-card :header="$t('pages.physicalObjects.crosses')">
          <label for="form-element-crossdesc">
            {{ $t("pages.physicalObjects.crossdesc") }}
          </label>
          <b-row v-if="draft['crossDesc'] != null">
            <b-col>
              <b-form-input
                id="form-element-location"
                v-model="draft['crossDesc']"
                @change="changed"
                @input="changed"
              />
            </b-col>
            <b-col sm="auto">
              <multiselect
                v-if="modernLanguages.length > 0"
                v-model="draft['crossDescLang'].id"
                :options="modernLanguages.map(i => i.id)"
                :custom-label="key => modernLanguages.find(x => x.id == key).sv"
                :show-labels="false"
                @select="changed"
              />
            </b-col>
          </b-row>
          <b-row v-if="draft['crossDesc'] == null">
            <b-col>
              <b-button
                v-t="'pages.physicalObjects.addCrossdesc'"
                @click="addCrossDesc"
              />
            </b-col>
          </b-row>
          <hr />
          <label for="form-element-crosses">
            {{ $t("pages.physicalObjects.crosses") }}
          </label>
          <crosses-component
            v-if="draft['crosses']"
            id="form-element-crosses"
            v-model="draft['crosses']"
            @change="changed"
          />
          <hr />
          <styles-component
            v-if="draft['styles']"
            id="form-element-styles"
            v-model="draft['styles']"
            @change="changed"
          />
        </collapsible-card>
        <collapsible-card :header="$t('pages.physicalObjects.locations')">
          <b-form-radio-group
            id="form-element-atOriginalLocation"
            v-model="draft['atOriginalLocation']"
            :options="[
              {
                text: $t('pages.physicalObjects.originalLocation'),
                value: true
              },
              {
                text: $t('pages.physicalObjects.notOriginalLocation'),
                value: false
              },
              {
                text: $t('pages.physicalObjects.unknownOriginalLocation'),
                value: null
              }
            ]"
            button-variant="outline-primary"
            buttons
            @change="changed"
          />
          <hr />
          <b-row>
            <b-col>
              <label for="form-element-place"
                >{{ $t("pages.physicalObjects.place") }}
                {{ $t("general.required") }}</label
              >
            </b-col>
          </b-row>
          <b-row v-if="draft['place']">
            <b-col>
              {{ places[draft["place"].id] }}
            </b-col>
            <b-col sm="auto">
              <b-button
                id="form-element-place"
                v-t="'pages.physicalObjects.changeplace'"
                @click="deletePlace"
              />
            </b-col>
          </b-row>
          <b-row v-if="!draft['place']">
            <b-col>
              {{ $t("pages.physicalObjects.usedToggle") }}
              <b-form-checkbox
                v-model="usedPlacesToggle"
                switch
                class="d-inline"
              >
                {{
                  usedPlacesToggle
                    ? $t("pages.physicalObjects.used")
                    : $t("pages.physicalObjects.unused")
                }}
              </b-form-checkbox>
            </b-col>
          </b-row>
          <b-row v-if="!draft['place']">
            <b-col>
              <base-auto-complete
                id="form-element-place"
                :graph-query="
                  usedPlacesToggle ? placeCompletions : unusedPlaceCompletions
                "
                :placeholder="$t('pages.physicalObjects.setplace')"
                @select="addPlace"
              >
                <!-- What to show when we have no results -->
                <template v-slot:noResult>
                  <span>{{ $t("components.baseList.noResult") }}</span>
                </template>
                <!-- Template for each element in the auto complete -->
                <template v-slot:option="{ option: { name } }">
                  <span>{{ name }}</span>
                </template>
                <!-- Template for a selected element in the auto complete -->
                <template v-slot:singleLabel="{ option: { name } }">
                  <span>{{ name }}</span>
                </template>
              </base-auto-complete>
            </b-col>
          </b-row>
          <hr />
          <label for="form-element-location">
            {{ $t("pages.physicalObjects.location") }}
          </label>
          <b-row v-if="draft['location'] != null">
            <b-col>
              <b-form-input
                id="form-element-location"
                v-model="draft['location']"
                @change="changed"
                @input="changed"
              />
            </b-col>
            <b-col sm="auto">
              <multiselect
                v-if="modernLanguages.length > 0"
                v-model="draft['locationLang'].id"
                :options="modernLanguages.map(i => i.id)"
                :custom-label="key => modernLanguages.find(x => x.id == key).sv"
                :show-labels="false"
                @select="changed"
              />
            </b-col>
          </b-row>
          <b-row v-if="draft['location'] == null">
            <b-col>
              <b-button
                v-t="'pages.physicalObjects.addlocation'"
                @click="addLocation"
              />
            </b-col>
          </b-row>
          <hr />
          <label for="form-element-originalLocation">
            {{ $t("pages.physicalObjects.originalLocation") }}
          </label>
          <b-row v-if="draft['originalLocation'] != null">
            <b-col>
              <b-form-input
                id="form-element-originalLocation"
                v-model="draft['originalLocation']"
                @change="changed"
                @input="changed"
              />
            </b-col>
            <b-col sm="auto">
              <multiselect
                v-if="modernLanguages.length > 0"
                v-model="draft['originalLocationLang'].id"
                :options="modernLanguages.map(i => i.id)"
                :custom-label="key => modernLanguages.find(x => x.id == key).sv"
                :show-labels="false"
                @select="changed"
              />
            </b-col>
          </b-row>
          <b-row v-if="draft['originalLocation'] == null">
            <b-col>
              <b-button
                v-t="'pages.physicalObjects.addoriginalLocation'"
                @click="addOriginalLocation"
              />
            </b-col>
          </b-row>
          <hr />
          <coordinates-component
            v-if="draft['coordinates']"
            id="form-element-coordinates"
            v-model="draft['coordinates']"
            @change="changed"
          />
        </collapsible-card>
        <collapsible-card :header="$t('pages.physicalObjects.imageLinks')">
          <b-list-group v-if="draft['imageLinks']">
            <b-list-group-item
              v-for="(item, index) in draft['imageLinks']"
              :key="'imageLink-' + index"
            >
              <b-row>
                <b-col>
                  <b-form-input
                    :id="'form-element-imageLink-' + index"
                    v-model="item.imageLink"
                    @change="changed"
                    @input="changed"
                  />
                </b-col>
                <b-col sm="auto">
                  <b-button
                    v-t="'pages.physicalObjects.imageLinkdelete'"
                    @click="deleteImageLink(index)"
                  />
                </b-col>
              </b-row>
            </b-list-group-item>
          </b-list-group>
          <b-button
            v-t="'pages.physicalObjects.imageLinkadd'"
            @click="addImageLink"
          />
        </collapsible-card>
        <collapsible-card :header="$t('pages.physicalObjects.fragments')">
          <label for="form-element-fragments">
            {{ $t("pages.physicalObjects.fragments") }}
          </label>
          <physical-objects-selector
            v-if="draft['fragments']"
            id="form-element-fragments"
            v-model="draft['fragments']"
            @change="changed"
          />
          <hr />
          <label for="form-element-belongsTo">
            {{ $t("pages.physicalObjects.belongsTo") }}
          </label>
          <physical-objects-selector
            v-if="draft['belongsTo']"
            id="form-element-belongsTo"
            v-model="draft['belongsTo']"
            @change="changed"
          />
        </collapsible-card>
        <collapsible-card :header="$t('pages.physicalObjects.findNumbers')">
          <b-list-group>
            <b-list-group-item
              v-for="(item, index) in draft['findNumbers']"
              :key="'findnumber-' + index"
            >
              <b-row>
                <b-col>
                  <b-form-input
                    :id="'form-element-findNumbers-' + index"
                    v-model="draft['findNumbers'][index]"
                    @change="changed"
                    @input="changed"
                  />
                </b-col>
                <b-col sm="auto">
                  <b-button
                    v-t="'pages.physicalObjects.findNumbersdelete'"
                    @click="deleteFindNumber(index)"
                  />
                </b-col>
              </b-row>
            </b-list-group-item>
          </b-list-group>
          <b-button
            v-t="'pages.physicalObjects.findNumbersnew'"
            @click="addFindNumber"
          />
        </collapsible-card>
        <collapsible-card :header="$t('pages.physicalObjects.miscellaneous')">
          <notes-component
            v-if="draft['notes']"
            id="form-element-notes"
            v-model="draft['notes']"
            @change="changed"
          />
          <hr />
          {{ $t("pages.bracteates.header") }}
          <b-list-group>
            <b-list-group-item
              v-for="(item, index) in draft['bracteatetypes']"
              :key="'bracteateType-' + index"
            >
              <b-row>
                <b-col>
                  <multiselect
                    v-if="bracteateTypes.length > 0"
                    v-model="item.id"
                    :options="bracteateTypes.map(i => i.id)"
                    :custom-label="
                      key => bracteateTypes.find(x => x.id == key).bracteateType
                    "
                    :show-labels="false"
                    @select="changed"
                  />
                </b-col>
                <b-col sm="auto">
                  <b-button
                    v-t="'pages.bracteates.delete'"
                    @click="deleteBracteateType(index)"
                  />
                </b-col>
              </b-row>
            </b-list-group-item>
          </b-list-group>
          <b-button v-t="'pages.bracteates.new'" @click="addBracteateType" />
          <hr />
          {{ $t("pages.uris.header") }}
          <uris-component
            v-if="draft['uris']"
            id="form-element-uris"
            v-model="draft['uris']"
            @change="changed"
          />
          <hr />
          {{ $t("pages.sources.header") }}
          <sources-selector
            v-if="draft['sources']"
            id="'form-element-sources"
            v-model="draft['sources']"
            @change="changed"
          />
        </collapsible-card>
        <collapsible-card :header="$t('pages.physicalObjects.her')" no-body>
          <b-list-group flush>
            <b-list-group-item>
              {{ $t("components.herSwedenWithNotes.header") }}
              <br />
              <her-sweden-with-notes
                v-if="
                  draft['herSwedenWithNotes'] &&
                    draft['herSwedenWithNotes'].length > 0
                "
                id="'form-element-herSwedenWithNotes'"
                v-model="draft['herSwedenWithNotes']"
                @change="changed"
              />
              <b-button
                v-if="draft['herSwedenWithNotes']"
                v-t="'components.herSwedenWithNotes.add'"
                @click="addHerSwedenWithNotes"
              />
            </b-list-group-item>
            <b-list-group-item>
              {{ $t("components.herNorwayWithNotes.header") }}
              <br />
              <her-norway-with-notes
                v-if="
                  draft['herNorwayWithNotes'] &&
                    draft['herNorwayWithNotes'].length > 0
                "
                id="'form-element-herNorwayWithNotes'"
                v-model="draft['herNorwayWithNotes']"
                @change="changed"
              />
              <b-button
                v-if="draft['herNorwayWithNotes']"
                v-t="'components.herNorwayWithNotes.add'"
                @click="addHerNorwayWithNotes"
              />
            </b-list-group-item>
            <b-list-group-item>
              {{ $t("components.herDenmarkWithNotes.header") }}
              <br />
              <her-denmark-with-notes
                v-if="
                  draft['herDenmarkWithNotes'] &&
                    draft['herDenmarkWithNotes'].length > 0
                "
                id="'form-element-herDenmarkWithNotes'"
                v-model="draft['herDenmarkWithNotes']"
                @change="changed"
              />
              <b-button
                v-if="draft['herDenmarkWithNotes']"
                v-t="'components.herDenmarkWithNotes.add'"
                @click="addHerDenmarkWithNotes"
              />
            </b-list-group-item>
            <b-list-group-item>
              {{ $t("components.herGreatBritain.header") }}
              <br />
              <her-great-britain
                v-if="
                  draft['herGreatBritain'] &&
                    draft['herGreatBritain'].length > 0
                "
                id="'form-element-herGreatBritain'"
                v-model="draft['herGreatBritain']"
                @change="changed"
              />
              <b-button
                v-if="draft['herGreatBritain']"
                v-t="'components.herGreatBritain.add'"
                @click="addHerGreatBritain"
              />
            </b-list-group-item>
          </b-list-group>
        </collapsible-card>
      </div>
      <hr />
      <b-form-group
        :label="$t('components.baseDetail.commentLabel')"
        label-for="draft-comment"
      >
        <b-form-textarea
          id="draft-comment"
          :key="'comment-' + commentKey"
          v-model="draftDetails.comment"
          :disabled="!edited && !isDraft"
          :placeholder="$t('components.baseDetail.commentPlaceHolder')"
          class="comment-area"
          rows="3"
          max-rows="15"
          @input="setEdited(true)"
        />
      </b-form-group>
      <b-form-group
        :label="$t('components.baseDetail.publicCommentLabel')"
        label-for="draft-public-comment"
      >
        <ckeditor
          v-if="edited || isDraft"
          v-model="draftDetails.publicComment"
          :editor="publicCommentEditor"
          :config="publicCommentEditorConfig"
          class="public-comment-area"
          @input="setEdited(true)"
        />
        <!-- Use class form-control and the readonly attribute to emulate a disabled textarea
             and get the same grayed-out look as the comment field. -->
        <div v-else class="form-control comment-disabled" readonly>
          {{
            draftDetails.publicComment || publicCommentEditorConfig.placeholder
          }}
        </div>
      </b-form-group>

      <b-form-row v-if="!edited && !isDraft" class="form-group">
        <b-col>
          <p v-t="'components.baseDetail.disabledCommentsExplanation'" />
        </b-col>
      </b-form-row>

      <b-form-row v-if="draft['place'] == null">
        <b-col>
          <p v-t="'pages.physicalObjects.disabledDueToMissingPlace'" />
        </b-col>
      </b-form-row>

      <b-form-row class="form-group form-group-buttons">
        <b-col lg="auto" md="12">
          <b-button
            key="button-save"
            v-t="'general.save'"
            :disabled="!edited"
            block
            class="mb-2"
            variant="primary"
            @click="save"
          />
        </b-col>
        <b-col lg="auto" md="12">
          <b-button
            key="button-reset"
            v-t="'general.reset'"
            :disabled="!edited"
            block
            class="mb-2"
            variant="outline-primary"
            @click="reset"
          />
        </b-col>
        <b-col v-if="!isDraft && !isNew" lg="auto" md="12">
          <b-button
            key="button-delete"
            v-t="localeSection + '.delete'"
            v-b-modal
            block
            class="mb-2"
            variant="outline-primary"
            @click="showModal('deleteEntity')"
          />
        </b-col>
        <b-col v-if="isDraft" lg="auto" md="12">
          <b-button
            key="button-delete-draft"
            v-t="'general.deleteDraft'"
            v-b-modal
            block
            class="mb-2"
            variant="outline-primary"
            @click="showModal('deleteDraft')"
          />
        </b-col>

        <b-col v-if="isDraft" lg="auto" md="12">
          <b-button
            key="button-publish"
            v-t="'general.publish'"
            v-b-modal
            block
            class="mb-2"
            variant="outline-primary"
            @click="showModal('publishDraft')"
          />
        </b-col>
      </b-form-row>
    </b-form>
  </b-container>
</template>

<script>
import gql from "graphql-tag";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import LeaveGuard from "@/mixins/LeaveGuard";

export default {
  name: "PhysicalObjectDetailPage",
  components: {
    CollapsibleCard: () => import("@/components/CollapsibleCard"),
    BaseDetailModals: () =>
      import("@/components/BaseDetailSimple/BaseDetailModals"),
    BaseAutoComplete: () => import("@/components/BaseAutoComplete"),
    // InscriptionsSelector: () => import("@/components/InscriptionsSelector"),
    ArtefactsSelector: () => import("@/components/ArtefactsSelector"),
    MaterialsSelector: () => import("@/components/MaterialsSelector"),
    DatingsComponent: () => import("@/components/DatingsComponent"),
    CrossesComponent: () => import("@/components/CrossesComponent"),
    PhysicalObjectsSelector: () =>
      import("@/components/PhysicalObjectsSelector"),
    NotesComponent: () => import("@/components/NotesComponent"),
    CoordinatesComponent: () => import("@/components/CoordinatesComponent"),
    UrisComponent: () => import("@/components/UrisComponent"),
    SourcesSelector: () => import("@/components/SourcesSelector"),
    StylesComponent: () => import("@/components/StylesComponent"),
    HerSwedenWithNotes: () => import("@/components/HerSwedenWithNotes"),
    HerNorwayWithNotes: () => import("@/components/HerNorwayWithNotes"),
    HerDenmarkWithNotes: () => import("@/components/HerDenmarkWithNotes"),
    HerGreatBritain: () => import("@/components/HerGreatBritain")
  },
  mixins: [LeaveGuard],
  data() {
    return {
      values: {},
      draft: {},
      draftDetails: {},
      edited: false,
      nilUUID: "00000000-0000-0000-0000-000000000000",
      // keys used to make sure b-form-textareas are updated, see https://github.com/bootstrap-vue/bootstrap-vue/issues/3103
      commentKey: 0,
      publicCommentKey: 0,
      publicCommentEditor: ClassicEditor,
      publicCommentEditorConfig: {
        placeholder: this.$t("components.baseDetail.publicCommentPlaceHolder"),
        toolbar: [
          "heading",
          "bold",
          "italic",
          "link",
          "bulletedList",
          "numberedList"
        ]
      },
      modals: {
        visible: {
          publishDraft: false,
          deleteEntity: false,
          deleteDraft: false,
          unsavedChanges: false
        },
        data: {
          deleteEntityName: this.entityName
        }
      },
      localeSection: "pages.physicalObjects",
      modernLanguages: [],
      bracteateTypes: [],
      places: {},
      usedPlacesToggle: false,
      inscriptions: [],
      queryValues: /* GraphQL */ `
        artefact
        place {
          id
          place
        }
        material
        extant
        atOriginalLocation
        datings {
          dating
          lang {
            id: language
          }
          sources {
            id
          }
        }
        tpq
        taq
        crossDesc
        crossDescLang {
          id: language
        }
        inscriptions {
          id
          signumInscriptions {
            canonical
            signum {
              signum1 {
                signum1
              }
              signum2
            }
          }
        }
        artefacts {
          id
        }
        materials {
          id
        }
        location
        locationLang {
          id: language
        }
        originalLocation
        originalLocationLang {
          id: language
        }
        imageLinks {
          imageLink
        }
        crosses {
          cross
          crossForms {
            certainty
            crossForm {
              id
            }
            sources {
              id
            }
          }
        }
        fragments {
          id
        }
        belongsTo {
          id
        }
        findNumbers
        notes {
          notes
          lang {
            id: language
          }
        }
        styles {
          style {
            id
          }
          certainty
          sources {
            id
          }
        }
        standardSigna {
          signum1
          signum2
        }
        bracteatetypes {
          id
        }
        coordinates {
          current
          latitude
          longitude
        }
        uris {
          id
        }
        sources {
          id
        }
        herSwedenWithNotes {
          herSweden {
            id
          }
          herSwedenNotes {
            lang {
              id: language
            }
            notes
          }
        }
        herNorwayWithNotes {
          herNorway {
            id
          }
          herNorwayNotes {
            lang {
              id: language
            }
            notes
          }
        }
        herDenmarkWithNotes {
          herDenmark {
            id
          }
          herDenmarkNotes {
            lang {
              id: language
            }
            notes
          }
        }
        herGreatBritain {
          id
        }
      `,
      placeCompletions: gql`
        query searchByPlace($search: String!) {
          completions: findByPlace(place: $search) {
            name: place
            id
          }
        }
      `,
      unusedPlaceCompletions: gql`
        query findUnusedPlaces {
          completions: findUnusedPlaces {
            name: place
            id
          }
        }
      `,
      wasValidated: false
    };
  },
  computed: {
    entityName() {
      return this.values ? this.displayName() : "";
    },
    uuid() {
      return this.$route.params.id;
    },
    title() {
      return (
        this.$t(this.localeSection + ".detailHeader") +
        (this.values ? ": " + this.displayName() : "")
      );
    },
    comment() {
      return this.draftDetails && this.draftDetails.comment
        ? this.draftDetails.comment
        : "";
    },
    publicComment() {
      return this.draftDetails && this.draftDetails.publicComment
        ? this.draftDetails.publicComment
        : "";
    },
    isDraft() {
      return this.draftDetails != null && this.draftDetails.draftId != null;
    },
    isNew() {
      return this.$route.params.id == this.nilUUID;
    },
    userName() {
      return this.$root.user !== undefined ? this.$root.user.username : null;
    },
    leaveGuardModalActive() {
      return this.leaveGuard.modalActive;
    }
  },
  watch: {
    edited(newEdited) {
      if (this.leaveGuard) {
        this.leaveGuard.hasChanged = newEdited;
      }
    },
    leaveGuardModalActive(modalActive) {
      if (modalActive) {
        this.showModal("unsavedChanges");
        // = this.leaveGuard.modalActive;
      }
    }
  },
  methods: {
    deleteEntity() {
      this.$apollo
        .mutate({
          mutation: gql`
            mutation deletePhysicalObject($id: ID) {
              deletePhysicalObject(id: $id)
            }
          `,
          // Parameters
          variables: { id: this.$route.params.id }
        })
        .then(() => {
          this.makeToast({
            message: this.$t("general.deleted"),
            variant: "success"
          });
          this.$router.push({
            name: this.$route.name,
            params: {
              id: this.nilUUID
            }
          });
          this.$apollo.queries.values.setVariables({ uuid: this.nilUUID });
        })
        .catch(error => {
          // eslint-disable-next-line no-console
          console.error(error);
          this.makeToast({
            message: this.$t("general.error"),
            variant: "danger"
          });
        });
    },
    deleteDraft() {
      this.$apollo
        .mutate({
          mutation: gql`
            mutation deletePhysicalObjectDraft($id: ID) {
              deletePhysicalObjectDraft(id: $id)
            }
          `,
          // Parameters
          variables: { id: this.draftDetails.draftId }
        })
        .then(() => {
          this.makeToast({
            message: this.$t("general.deleted"),
            variant: "success"
          });
          if (this.draftDetails.originalId == this.draftDetails.draftId) {
            this.$router.push({
              name: this.$route.name,
              params: {
                id: this.nilUUID
              }
            });
            this.$apollo.queries.values.setVariables({ uuid: this.nilUUID });
          } else {
            this.$apollo.queries.values.refetch();
          }
        })
        .catch(error => {
          // eslint-disable-next-line no-console
          console.error(error);
          this.makeToast({
            message: this.$t("general.error"),
            variant: "danger"
          });
        });
    },
    publish() {
      this.$apollo
        .mutate({
          mutation: gql`
            mutation publishPhysicalObject($id: ID) {
              publishPhysicalObject(id: $id)
            }
          `,
          // Parameters
          variables: { id: this.draftDetails.originalId }
        })
        .then(() => {
          this.makeToast({
            message: this.$t("general.published"),
            variant: "success"
          });
          this.$apollo.queries.values.refetch();
        })
        .catch(error => {
          // eslint-disable-next-line no-console
          console.error(error);
          this.makeToast({
            message: this.$t("general.error"),
            variant: "danger"
          });
        });
    },
    save() {
      if (this.validateForm()) {
        this.$apollo
          .mutate({
            mutation: gql`
              mutation updatePhysicalObject($input: PhysicalObjectInput) {
                updatePhysicalObject(input: $input) {
                  id
                }
              }
            `,
            variables: {
              input: {
                id: this.$route.params.id,
                ...this.draft,
                comment: this.comment,
                publicComment: this.publicComment
              }
            }
          })
          .then(data => {
            this.setEdited(false);
            this.makeToast({
              message: this.$t("general.saved"),
              variant: "success"
            });
            if (this.isNew) {
              this.$router.push({
                name: this.$route.name,
                params: {
                  id: data.data[Object.keys(data.data)[0]].id
                }
              });
              this.$apollo.queries.values.setVariables({
                uuid: data.data[Object.keys(data.data)[0]].id
              });
            } else {
              this.$apollo.queries.values.refetch();
            }
          })
          .catch(error => {
            // eslint-disable-next-line no-console
            console.error(
              "Could not save. Reason from server: " + error.message
            );
            this.makeToast({
              message: this.$t("general.error"),
              variant: "danger"
            });
          });
      } else {
        this.makeToast({
          message:
            this.$t("general.notSaved") +
            (this.draft["place"] == null
              ? "\n" + this.$t("pages.physicalObjects.placeError")
              : ""),
          variant: "danger"
        });
      }
    },
    changed() {
      this.wasValidated = false;
      // if not yet draft and first edit, add time stamp to comment
      if (!this.isDraft && !this.edited) {
        this.draftDetails.comment =
          new Date().toLocaleString(this.$t("general.locale.swedish")) +
          (this.userName ? " (" + this.userName + ")" : "") +
          "\n" +
          this.$t("components.baseDetail.newCommentHere") +
          "\n\n" +
          this.comment;
      }
      // make sure b-form-textarea is updated, see https://github.com/bootstrap-vue/bootstrap-vue/issues/3103
      this.commentKey++;

      this.setEdited(true);
    },
    setEdited(newEdited) {
      this.edited = newEdited;
      this.leaveGuard.hasChanged = true;
    },
    reset() {
      this.values = {};
      this.draft = {};
      this.setEdited(false);

      this.$apollo.queries.values.refetch();
    },
    makeToast({ title, message, append, variant, toaster }) {
      this.$bvToast.toast(message, {
        title: title || this.$t("general.toastTitle"),
        solid: true,
        autoHideDelay: 5000,
        appendToast: append,
        variant,
        toaster: toaster || "b-toaster-top-center"
      });
    },
    showModal(type) {
      this.modals.visible[type] = true;
    },
    scrubProperties(object) {
      for (var key in object) {
        if (object.hasOwnProperty(key)) {
          if (object[key] != null && typeof object[key] == "object") {
            this.scrubProperties(object[key]);
          } else {
            if (key[0] == "_") {
              delete object[key];
            }
          }
        }
      }
    },
    sortCrossForms(crossForms) {
      let copy = crossForms.map(
        ({ certainty, crossForm: { aspect, form, cross } }) => ({
          certainty,
          crossForm: {
            aspect,
            form,
            cross
          }
        })
      );

      copy.sort((crossForm0, crossForm1) =>
        (crossForm0.crossForm.aspect + crossForm0.crossForm.form).localeCompare(
          crossForm1.crossForm.aspect + crossForm1.crossForm.form,
          "sv"
        )
      );
      return copy;
    },
    displayName() {
      let name = this.values.artefact || this.$t("pages.artefacts.unknown");
      if (this.values.standardSigna && this.values.standardSigna.length > 0) {
        name += " (";
        name += this.values.standardSigna
          .map(signum => signum.signum1 + " " + signum.signum2)
          .join(", ");
        name += ")";
      }
      return name;
    },
    inscription(inscription) {
      let primary,
        secondary = [];
      inscription.signumInscriptions.forEach(element => {
        let signum =
          element.signum.signum1.signum1 + " " + element.signum.signum2;
        if (element.canonical) {
          primary = signum;
        } else {
          secondary.push(signum);
        }
      });
      return (
        primary +
        (secondary.length > 0
          ? " (" +
            secondary.sort((a, b) => a.localeCompare(b, "sv")).join(", ") +
            ")"
          : "")
      );
    },
    addLocation() {
      this.draft["location"] = "";
      this.draft["locationLang"] = { id: "sv-se" };
      this.changed();
    },
    addOriginalLocation() {
      this.draft["originalLocation"] = "";
      this.draft["originalLocationLang"] = { id: "sv-se" };
      this.changed();
    },
    addCrossDesc() {
      this.draft["crossDesc"] = "";
      this.draft["crossDescLang"] = { id: "sv-se" };
      this.changed();
    },
    deletePlace() {
      this.draft["place"] = null;
      this.changed();
    },
    addPlace(element) {
      this.draft["place"] = { id: element.id };
      this.places[element.id] = element.name;
      this.changed();
    },
    addImageLink() {
      this.draft["imageLinks"].push({ imageLink: "" });
      this.changed();
    },
    deleteImageLink(index) {
      this.draft["imageLinks"].splice(index, 1);
      this.changed();
      this.changed();
    },
    addFindNumber() {
      this.draft["findNumbers"].push("");
      this.changed();
    },
    deleteFindNumber(index) {
      this.draft["findNumbers"].splice(index, 1);
      this.changed();
    },
    addBracteateType() {
      this.draft["bracteatetypes"].push({ id: null });
      this.changed();
    },
    deleteBracteateType(index) {
      this.draft["bracteatetypes"].splice(index, 1);
      this.changed();
    },
    addHerSwedenWithNotes() {
      this.draft["herSwedenWithNotes"].push({
        herSweden: { id: null },
        herSwedenNotes: []
      });
      this.changed();
    },
    addHerNorwayWithNotes() {
      this.draft["herNorwayWithNotes"].push({
        herNorway: { id: null },
        herNorwayNotes: []
      });
      this.changed();
    },
    addHerDenmarkWithNotes() {
      this.draft["herDenmarkWithNotes"].push({
        herDenmark: { id: null },
        herDenmarkNotes: []
      });
      this.changed();
    },
    addHerGreatBritain() {
      this.draft["herGreatBritain"].push({ id: null });
      this.changed();
    },
    validateForm() {
      // Since only `place` is required, and we have no other restrictions, this check is very simple.
      // If more fields will be required in the future, this will have to be changed into a more
      // complex check.
      // See validateForm() in BaseDetailSimple/detail/index.vue for code that does this.
      let formIsValid = this.draft["place"] != null;

      this.wasValidated = true;
      return formIsValid;
    }
  },
  metaInfo() {
    return {
      title: this.title
    };
  },
  apollo: {
    values() {
      return {
        query: gql`
          query physicalObject($uuid: ID) {
            values: physicalObject(id: $uuid) {
              ${this.queryValues}
            }
            draft: physicalObjectDraft(id: $uuid) {
              ${this.queryValues}
            }
            draftDetails: draft(originalId: $uuid) {
              originalId
              draftId
              comment
              publicComment
            }
            modernLanguages {
              id: language
              sv
            }
            bracteateTypes {
              id
              bracteateType
            }
          }
        `,
        variables: { uuid: this.uuid },
        result(result) {
          if (result.stale) {
            // result is called again with stale = true
            return;
          }
          var _this = this;

          this.values = result.data.values || {};

          // Make draft a copy of the current entity if no draft exists
          this.draft =
            result.data.draft || JSON.parse(JSON.stringify(this.values));
          // Get rid of __typename field and all fields starting with _
          // (See warning at https://akryum.github.io/vue-apollo/guide/apollo/mutations.html)
          this.scrubProperties(this.draft);

          // pre-load current place name value and remove from further processing since
          // place name is not part of input for saving
          if (this.draft["place"] != null) {
            this.places[this.draft["place"].id] = this.draft["place"].place;
            delete this.draft["place"]["place"];
          }

          // remove standardSigna from draft since it's only informational data not to be saved
          delete this.draft.standardSigna;

          // Either from original or draft. Not part of input
          delete this.draft["inscriptions"];

          this.draftDetails = result.data.draftDetails || {
            comment: "",
            publicComment: ""
          };

          // Make sure b-form-textareas are updated, see https://github.com/bootstrap-vue/bootstrap-vue/issues/3103
          this.commentKey++;
          this.publicCommentKey++;

          this.modernLanguages = result.data.modernLanguages.sort((a, b) =>
            a.sv.localeCompare(b.sv, "sv")
          );

          this.bracteateTypes = result.data.bracteateTypes.sort((a, b) =>
            a.bracteateType == "Okänd"
              ? false
              : a.bracteateType.localeCompare(b.bracteateType, "sv")
          );
        }
      };
    }
  }
};
</script>
